<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Placeholder, Text } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import gsap from 'gsap';
import componentFactory from '@/temp/componentFactory';
import type { SiteTabsItemFields, SiteTabsProps } from '@/types/components/SiteTabs';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';
import { eq } from '@/utils/string-utils';

type TabSource = ScItem<SiteTabsItemFields> & {
  uid?: string;
  rendering?: ComponentRendering;
};

type DynamicComponentItem = ComponentRendering & {
  id?: string;
  template?: string;
  templateName?: string;
};

interface NormalizedTab extends TabSource {
  tabId: string;
  titleField: SiteTabsItemFields['title'];
  titleValue: string;
  anchorId: string;
  anchorKey: string;
  components: DynamicComponentItem[];
}

const props = defineProps<SiteTabsProps>();

const activeTabId = ref<string>();
const animating = ref(false);
const scrollSyncLocked = ref(false);
const snapped = ref(false);
const headerOffset = ref(0);
const tabBarHeight = ref(0);
const rootEl = ref<HTMLElement | null>(null);
const headerScrollEl = ref<HTMLElement | null>(null);

const fields = computed(() => props.fields || {});
const legacyTabs = computed<ComponentRendering[]>(
  () => props.rendering?.placeholders?.['site-tab-items'] ?? []
);
const dataTabs = computed<TabSource[]>(() => fields.value.items ?? []);
const isDataDriven = computed(() => dataTabs.value.length > 0);

const displayMode = computed(() => {
  const displayModeField = fields.value.displayMode as any;
  const directValue = displayModeField?.value;
  if (typeof directValue === 'string' && directValue) {
    return directValue;
  }

  const optionValue = utils.sp.getVal(displayModeField);
  if (typeof optionValue === 'string' && optionValue) {
    return optionValue;
  }

  return 'anchor';
});
const isSwitchMode = computed(() => displayMode.value === 'switch');
const snapToTop = computed(() => true);

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const headerStyle = computed(() => {
  const style: Record<string, string> = {
    '--site-tabs-header-offset': `${headerOffset.value}px`,
  };

  return style;
});

const headerShellStyle = computed(() => {
  if (!snapToTop.value || !snapped.value || !tabBarHeight.value) return {};

  return {
    minHeight: `${tabBarHeight.value}px`,
  };
});

const rootClass = computed(() =>
  utils.overall.baseComClass(fields.value, 'c-site-tabs', {
    'snap-top': snapToTop.value,
    animating: animating.value,
    snapped: snapped.value,
    [`c-site-tabs--${displayMode.value}`]: true,
  })
);

const pageAttrs = computed(() => (props.page ? { page: props.page } : {}));
const activeTab = computed(() => tabs.value.find((tab) => tab.tabId === activeTabId.value));
const visibleTabs = computed(() =>
  isSwitchMode.value && activeTab.value ? [activeTab.value] : tabs.value
);

function getFieldValue(field?: string | { value?: string }) {
  if (typeof field === 'string') return field;
  return field?.value ?? '';
}

function stripHtml(value?: string) {
  return `${value ?? ''}`
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slugify(value?: string) {
  return stripHtml(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'ImageTextCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getTabKey(item: TabSource, index: number) {
  return (
    getFieldValue(item?.fields?.key) ||
    item?.id ||
    item?.uid ||
    item?.rendering?.uid ||
    `tab-${index + 1}`
  );
}

function getTabTitleField(item: TabSource): SiteTabsItemFields['title'] {
  return item?.fields?.title ?? { value: item?.displayName ?? item?.name ?? '' };
}

function getTabTitleValue(item: TabSource) {
  return stripHtml(getFieldValue(getTabTitleField(item)));
}

function getTabAnchorId(item: TabSource, index: number) {
  const base = isDataDriven.value
    ? slugify(getFieldValue(item?.fields?.key)) || slugify(getTabTitleValue(item))
    : item?.rendering?.uid || item?.uid || item?.id;

  return `tab-${base || index + 1}`;
}

const tabs = computed<NormalizedTab[]>(() => {
  const source = isDataDriven.value ? dataTabs.value : (legacyTabs.value as unknown as TabSource[]);

  return source.map((item, index) => {
    const tabId = item?.id || item?.uid || item?.rendering?.uid || `tab-${index + 1}`;

    return {
      ...item,
      tabId,
      titleField: getTabTitleField(item),
      titleValue: getTabTitleValue(item),
      anchorId: getTabAnchorId(item, index),
      anchorKey: getTabKey(item, index),
      components: isDataDriven.value
        ? (item?.fields?.components ?? []).map((componentData) => ({
            ...componentData,
            componentName: getComponentName(componentData),
          }))
        : [],
    };
  });
});

watch(
  tabs,
  (list) => {
    if (!list.length) {
      activeTabId.value = undefined;
      return;
    }

    if (!list.some((tab) => tab.tabId === activeTabId.value)) {
      activeTabId.value = list[0].tabId;
    }
  },
  { immediate: true }
);

function updateActiveTab(tab: NormalizedTab) {
  activeTabId.value = tab.tabId;
  animating.value = true;
}

function updateHeaderOffset() {
  if (typeof window === 'undefined') return;

  const headerEl =
    window.document.querySelector('#header-content .c-gen-nav') ??
    window.document.querySelector('.c-gen-nav');
  const headerTopEl = headerEl?.querySelector('.c-gen-nav__top');
  const topRect = headerTopEl?.getBoundingClientRect?.();
  const nextOffset = Math.round(topRect?.bottom ?? headerEl?.getBoundingClientRect?.().bottom ?? 0);

  headerOffset.value = nextOffset;
}

function updateTabBarMetrics() {
  if (!headerScrollEl.value) return;

  tabBarHeight.value = Math.round(headerScrollEl.value.offsetHeight ?? 0);
}

function applyHashTab() {
  if (typeof window === 'undefined') return;

  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (!hash) return;

  const tab = tabs.value.find(
    (item) => eq(item.titleValue, hash) || eq(item.anchorId, hash) || eq(item.anchorKey, hash)
  );

  if (tab) activeTabId.value = tab.tabId;
}

function scrollToTab(tab: NormalizedTab, behavior: ScrollBehavior = 'smooth') {
  if (isSwitchMode.value) return;
  if (!tab?.anchorId) return;

  const anchorEl = document.getElementById(tab.anchorId);
  if (!anchorEl) return;

  const stickyOffset = tabBarHeight.value + headerOffset.value;
  const top = window.scrollY + anchorEl.getBoundingClientRect().top - stickyOffset - 12;
  window.scrollTo({ top, behavior });

  if (window.history?.replaceState) {
    window.history.replaceState(null, '', `#${tab.anchorId}`);
  } else {
    window.location.hash = tab.anchorId;
  }
}

function syncActiveTabByScroll() {
  if (isSwitchMode.value) return;
  if (!tabs.value.length || scrollSyncLocked.value) return;

  const stickyOffset = tabBarHeight.value + headerOffset.value;
  const detectLine = stickyOffset + 20;
  let nextActive = tabs.value[0];

  for (const tab of tabs.value) {
    const anchorEl = document.getElementById(tab.anchorId);
    if (!anchorEl) continue;

    const top = anchorEl.getBoundingClientRect().top;
    if (top <= detectLine) {
      nextActive = tab;
    } else {
      break;
    }
  }

  if (nextActive?.tabId !== activeTab.value?.tabId) {
    activeTabId.value = nextActive?.tabId;
    animating.value = false;
  }
}

let scrollHandler: (() => void) | undefined;
let userScrollIntentHandler: ((event: WheelEvent | TouchEvent | KeyboardEvent) => void) | undefined;

function unlockScrollSync() {
  if (!scrollSyncLocked.value) return;
  scrollSyncLocked.value = false;
  animating.value = false;
}

onMounted(() => {
  nextTick(() => {
    updateHeaderOffset();
    updateTabBarMetrics();
    applyHashTab();
    syncActiveTabByScroll();
  });

  scrollHandler = () => {
    if (!rootEl.value || !headerScrollEl.value) return;

    updateHeaderOffset();
    updateTabBarMetrics();
    const rect = rootEl.value.getBoundingClientRect();
    snapped.value = !!snapToTop.value && rect.top <= headerOffset.value;
    syncActiveTabByScroll();
  };

  userScrollIntentHandler = (event) => {
    if (!scrollSyncLocked.value) return;

    if (event.type === 'keydown') {
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (!scrollKeys.includes((event as KeyboardEvent).key)) return;
    }

    unlockScrollSync();
  };

  window.addEventListener('scroll', scrollHandler, { passive: true });
  window.addEventListener('resize', scrollHandler);
  window.addEventListener('wheel', userScrollIntentHandler, { passive: true });
  window.addEventListener('touchmove', userScrollIntentHandler, { passive: true });
  window.addEventListener('keydown', userScrollIntentHandler);
  scrollHandler();
});

onBeforeUnmount(() => {
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
    window.removeEventListener('resize', scrollHandler);
  }
  if (userScrollIntentHandler) {
    window.removeEventListener('wheel', userScrollIntentHandler);
    window.removeEventListener('touchmove', userScrollIntentHandler);
    window.removeEventListener('keydown', userScrollIntentHandler);
  }
});

function onDesktopTabClick(event: MouseEvent, tab: NormalizedTab) {
  event.preventDefault();
  if (!headerScrollEl.value || tab?.tabId === activeTab.value?.tabId) return;

  updateActiveTab(tab);

  if (isSwitchMode.value) {
    return;
  }

  scrollSyncLocked.value = true;
  scrollToTab(tab);

  if (headerScrollEl.value.scrollWidth === headerScrollEl.value.offsetWidth) return;

  const el = event.currentTarget as HTMLElement;
  let left = el.offsetLeft + el.offsetWidth / 2 - headerScrollEl.value.offsetWidth / 2 + 24;
  if (left < 0) left = 0;

  gsap.killTweensOf([headerScrollEl.value]);
  gsap.to(headerScrollEl.value, {
    scrollLeft: left,
    duration: 1,
  });
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
    page: props.page,
  };
}
</script>

<template>
  <div ref="rootEl" :class="rootClass" :style="rootStyle" :data-id="rendering?.uid">
    <div class="c-site-tabs__header-shell" :style="headerShellStyle">
      <div ref="headerScrollEl" class="s-sth" :style="headerStyle" role="tablist">
        <div class="s-sth__box">
          <div class="s-sth__content">
            <button
              v-for="(tab, index) in tabs"
              :key="tab.tabId ?? index"
              :class="['s-sth__tab', { active: activeTab?.tabId === tab.tabId }]"
              type="button"
              role="tab"
              :aria-selected="activeTab?.tabId === tab.tabId"
              :aria-controls="tab.anchorId"
              :title="tab.titleValue"
              @click="onDesktopTabClick($event, tab)"
            >
              <Text :field="tab.titleField" />
              <span v-if="activeTab?.tabId === tab.tabId" class="s-sth__indicator" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="c-site-tabs__main">
      <template v-if="isDataDriven">
        <section
          v-for="(tab, index) in visibleTabs"
          :id="tab.anchorId"
          :key="tab.tabId ?? index"
          class="c-site-tabs__panel"
          role="tabpanel"
        >
          <component
            :is="resolveDynamicComponent(componentData)"
            v-for="(componentData, componentIndex) in tab.components"
            v-bind="getDynamicComponentProps(componentData)"
            :key="componentData?.uid ?? componentData?.id ?? `${tab.tabId}-${componentIndex}`"
          />
        </section>
      </template>

      <Placeholder v-else name="site-tab-items" :rendering="rendering" v-bind="pageAttrs" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-site-tabs {
  display: block;
  position: relative;
  background-color: var(--back-color);
  color: var(--color);

  &__main,
  &__panel,
  &__header-shell {
    position: relative;
  }

  &__header-shell {
    z-index: 26;
  }

  &--edit {
    display: flex;
    flex-direction: column;
    gap: $space-24;
  }

  &.snap-top {
    position: relative;
    z-index: 26;
  }

  &.snapped {
    .s-sth {
      position: fixed;
      top: var(--site-tabs-header-offset, 0px);
      left: 50%;
      z-index: 26;
      width: 100%;
      transform: translateX(-50%);
      background-color: var(--back-color, $white);
      border: none;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    }

    @include tablet-landscape {
      .s-sth {
        width: calc(100% - #{grid-width(2)});
      }
    }
  }

  .s-sth {
    $c: &;
    display: block;
    width: 100%;
    overflow-x: auto;
    border-bottom: 1px solid $grey;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &__box {
      position: relative;
      width: 100%;
      padding: 0 grid-width(1);
    }

    &__content {
      display: flex;
      align-items: center;
      gap: 24px;
      min-width: max-content;
    }

    &__tab {
      position: relative;
      height: 56px;
      padding: 0;
      border: 0;
      color: #6f6f6f;
      background: transparent;
      cursor: pointer;
      font: inherit;
      line-height: 56px;
      white-space: nowrap;

      &.active {
        color: #739b78;
        cursor: default;
        font-weight: 600;
      }
    }

    &__indicator {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 2px;
      background-color: #739b78;
    }

    &.as-button {
      justify-content: flex-start;
      border-bottom: none;

      #{$c}__tab {
        padding: $space-12 $space-24;
        color: #6f6f6f;
        background-color: color-mix(in srgb, #739b78 10%, transparent);
        border-radius: 4px;

        &:hover {
          background-color: color-mix(in srgb, #739b78 40%, transparent);
        }

        &.active {
          color: $white;
          background-color: #739b78;
        }
      }

      #{$c}__indicator {
        display: none;
      }
    }
  }

  @include tablet {
    padding: $space-24 0;

    .c-site-tabs__header-shell {
      padding: 0;
      background: $white;

      .s-sth {
        width: 100%;
        padding: 0 grid-width(2);

        &__box {
          max-width: 1440px;
          margin: 0 auto;
          padding: 0;
        }

        &__content {
          gap: 40px;
        }

        &__tab {
          height: 72px;
          line-height: 72px;
        }
      }
    }
  }
}
</style>
